/**
 * Created by murat on 9/27/2016.
 */
import {Injectable} from '@angular/core';
import { Observable, Subject, empty } from 'rxjs';
import {AppSettings} from '@slarys/ufe-config';
import {User, Notification} from '@slarys/ufe-models';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {debounceTime, distinctUntilChanged, retry, tap} from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private _users: Observable<Array<User>>;
  private _user: Observable<User>;
  private _loggedInUser: User;
  private newUserSignupSubject = new Subject<any>();
  private loggedOut = new Subject<any>();
  private loggedIn = new Subject<any>();
  private httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    })
  };

  public onLoggedOut(): Observable<any> {
    return this.loggedOut.asObservable();
  }

  public onLoggedIn(): Observable<any> {
    return this.loggedIn.asObservable();
  }

  public getnewUserSignup(): Observable<any> {
    return this.newUserSignupSubject.asObservable();
  }

  get loggedInUser(): User {
    return this._loggedInUser;
  }

  set loggedInUser(value: User) {
    this._loggedInUser = value;
  }
  get user(): Observable<User> {
    return this._user;
  }

  set user(value: Observable<User>) {
    this._user = value;
  }
  get users(): Observable<Array<User>> {
    return this._users;
  }

  set users(value: Observable<Array<User>>) {
    this._users = value;
  }

  constructor(private http: HttpClient) {
  }

  public notifynewUserSignup(result: boolean) {
    this.newUserSignupSubject.next(result);
  }

  // public getCorporateUsers (page: number): Observable<any> {
  //   let url = AppSettings.API_ENDPOINT + '/users/get-corporate-users';
  //   url += '?page=' + page;
  //
  //   return this.http.get(url);
  // }

  public getMessageData (): Observable<any> {
    const url = AppSettings.API_ENDPOINT + '/users/message-data';
    return this.http.get(url);
  }

  public getUser (id: string): Observable<any> {
    return this.http.get(AppSettings.API_ENDPOINT + '/users/user/' + id).pipe();

  }

  public fetchLoggedInUser(): Observable<any> {
    return this.http.get(AppSettings.API_ENDPOINT + '/users/me').pipe(
        tap(response => {
          this.loggedIn.next(true);
        })
    );
  }

  public checkEmailNotTaken(email): Observable<any> {
    if (!email) { return empty(); }
    return this.http.get(AppSettings.API_ENDPOINT + '/auth/check-email/' + email).pipe(
      retry(3),
      debounceTime(500),
      distinctUntilChanged());
  }

  public signInAsAppUser(user: User) {
    return this.http
        .post<User>(
            AppSettings.API_ENDPOINT + '/auth/signin-app',
            user,
            this.httpOptions
        )
        .pipe(
            tap(response => {
              this.loggedIn.next(true);
            }));
  }

  public signIn(user: User) {
    return this.http
      .post<User>(
        AppSettings.API_ENDPOINT + '/auth/login',
        user,
        this.httpOptions
      )
        .pipe(
            tap(response => {
              this.loggedIn.next(true);
            }));
  }

  public signInSocial(provider, token) {
    return this.http
        .get(
            AppSettings.API_ENDPOINT + `/auth/${provider}?access_token=${token}`);
  }

  public forgotPassword(user: User) {
    return this.http
      .post<User>(
        AppSettings.API_ENDPOINT + '/auth/forgot-password',
        user,
        this.httpOptions
      )
      .pipe
      // catchError
      ();
  }

  public resetPassword(credentials) {
    return this.http
      .post<any>(
        AppSettings.API_ENDPOINT + '/auth/reset-password',
        credentials,
        this.httpOptions
      )
      .pipe
      // catchError
      ();
  }

  public verifyEmail(token) {
    return this.http
      .post<any>(
        AppSettings.API_ENDPOINT + '/auth/verify-email',
        {token},
        this.httpOptions
      )
      .pipe
      // catchError
      ();
  }

  public checkEmail(email) {
    return this.http
        .get(AppSettings.API_ENDPOINT + '/auth/verify-email/' + email);
  }

  public changePassword(credentials) {
    return this.http
      .post<any>(
        AppSettings.API_ENDPOINT + '/auth/change-password',
        credentials,
        this.httpOptions
      )
      .pipe
      // catchError
      ();
  }

  public saveUserOrCorporateUser(user: User) {
    const url = user.accountType === '1' ? '/auth/create-corporate' : '/auth/create';
    return this.http
        .post<User>(
            AppSettings.API_ENDPOINT + url,
            user,
            this.httpOptions
        )
        .pipe(
            tap(response => {
              this.loggedIn.next(true);
            }));
  }

  public saveAppCorporateUser(user: User) {
    const url = '/auth/signup-app-corporate';
    return this.http
        .post<User>(
            AppSettings.API_ENDPOINT + url,
            user,
            this.httpOptions
        )
        .pipe(
            tap(response => {
              this.loggedIn.next(true);
            }));
  }

  public saveAppUser(user: User) {
    const url = '/auth/signup-app';
    return this.http
        .post<User>(
            AppSettings.API_ENDPOINT + url,
            user,
            this.httpOptions
        )
        .pipe(
            tap(response => {
              this.loggedIn.next(true);
            }));
  }

  public removeProvider(provider) {
    const response: any =
      this.http.delete(AppSettings.API_ENDPOINT + '/auth/remove-provider?provider=' + provider);
    return response;
  }

  public getNotifications() {
    const response: any =
      this.http.get(AppSettings.API_ENDPOINT + '/users/get-notifications');
    return response;
  }

  public updateNotification(notification: Notification) {
    return this.http
      .post<Notification>(
        AppSettings.API_ENDPOINT + '/users/update-notifications',
        notification,
        this.httpOptions
      )
      .pipe
      // catchError
      ();
  }

  public validateResetToken(token) {
    return this.http
        .post<any>(
            AppSettings.API_ENDPOINT + '/auth/validate-reset-password-token',
            token,
            this.httpOptions
        )
        .pipe();
  }

  public saveLocalUser(user: User) {
    return this.http
      .post<User>(
        AppSettings.API_ENDPOINT + '/auth/create',
        user,
        this.httpOptions
      )
        .pipe(
            tap(response => {
              this.loggedIn.next(true);
            }));
  }

  public updateProfileImage(image) {
    return this.http
      .post<any>(
        AppSettings.API_ENDPOINT + '/users/change-profile-photo',
        image,
        this.httpOptions
      )
      .pipe
      // catchError
      ();
  }

  public upgradeToCorporateAccount(user: User) {
    return this.http
      .post<User>(
        AppSettings.API_ENDPOINT + '/users/upgrade',
        user,
        this.httpOptions
      )
      .pipe
      // catchError
      ();
  }

  public getTaxOffices(id: string): Observable<any> {
    const response = this.http.get(AppSettings.API_ENDPOINT + '/misc/taxOffices/' + id);
    return response;
  }

  public saveCorporateUser(user: User) {
    return this.http
      .put<User>(
        AppSettings.API_ENDPOINT + '/auth/create-corporate',
        user,
        this.httpOptions
      )
      .pipe
      // catchError
      ();
  }

  public updateUser(user: User) {
    return this.http
      .post<User>(
        AppSettings.API_ENDPOINT + '/users/update',
        user,
        this.httpOptions
      )
      .pipe
      // catchError
      ();
  }

  public updateCorporateUser(user: User) {
    return this.http
      .post<User>(
        AppSettings.API_ENDPOINT + '/users/update',
        user,
        this.httpOptions
      )
      .pipe
      // catchError
      ();
  }

  resendVerifyEmail(email: any) {
    return this.http
      .post<Notification>(
        AppSettings.API_ENDPOINT + '/auth/resend-verify-email',
        {email},
        this.httpOptions
      )
      .pipe
      // catchError
      ();
  }

  addSocialMediaLoginMissingFields(user: User) {
    return this.http
      .post<User>(
        AppSettings.API_ENDPOINT + '/users/addSocialMediaLoginMissingFields',
        user,
        this.httpOptions
      )
      .pipe
      // catchError
      ();
  }

  logout() {
    return this.http
        .post<any>(
            AppSettings.API_ENDPOINT + '/auth/logout',
            null,
            this.httpOptions
        )
        .pipe
        // catchError
        (tap(response => {
          this.loggedInUser = new User();
          this.loggedOut.next(true);
        }));
  }
}
